import getIntl from 'Helpers/intl'
import {
  STORE_KEY_ID_LIST,
  notifyParent,
  sessionStoreGet,
  sessionStoreSet,
  storeGet,
} from 'mailup-react-helpers'

import { TOAST_VARIANT_SUCCESS, toastShow } from 'mailup-web-components'

import { STORE_KEY__RECIPIENTS_LIST_MODAL_DELETE_PREFS } from 'Constants'

import {
  OPTOUT_TYPE_OBLIO,
  OPTOUT_TYPE_PLATFORM,
  beRecipientAddGroup,
  beRecipientDeleteRecipient,
  beRecipientGetListOfGroupDetails,
  beRecipientOptoutEmailRecipientFromList,
  beRecipientOptoutSmsRecipientFromList,
} from 'Api/beRecipientApi'

import {
  beRecipientApiGetAllSubscriptionsEmailFromList,
  beRecipientApiGetAllSubscriptionsSmsFromList,
  beRecipientApiOptoutEmailRecipientFromMultiLists,
  beRecipientApiOptoutSmsRecipientFromMultiLists,
} from 'Api/beRecipientApiTS'

import type {
  ModalDeleteState,
  ModalFilterDetailsState,
} from 'Pages/RecipientsList/model'

import type {
  OptOutMultiListDataIn,
  OptoutRecipientFromMultiListsQsParams,
} from 'Types/beRecipientsApi'

import type { LabelValuePair } from 'Types/common'

export const unsubscribeRecipient = async (
  idRecipient: number,
  channelEmail: boolean,
  channelSMS: boolean,
  wipeData: boolean,
): Promise<void> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const flags = {
    changeFromOptin: true,
    changeFromPending: true,
    changeFromOptout: true,
  }

  const optoutType = wipeData ? OPTOUT_TYPE_OBLIO : OPTOUT_TYPE_PLATFORM

  const apiCalls = [
    channelEmail
      ? beRecipientOptoutEmailRecipientFromList(
          idRecipient,
          idList,
          flags,
          optoutType,
        )
      : undefined,
    channelSMS
      ? beRecipientOptoutSmsRecipientFromList(
          idRecipient,
          idList,
          flags,
          optoutType,
        )
      : undefined,
  ].filter((p) => p !== undefined)

  await Promise.all(apiCalls)
}

export const notifyUnsubscription = (
  lists: number[],
  allLists: boolean,
): void => {
  const isMulti = allLists || lists.length > 1

  if (isMulti) {
    toastShow(
      getIntl().formatMessage({
        id: 'Load.RecipientsList.UnsubscribeSuccessMultipleLists',
      }),
      TOAST_VARIANT_SUCCESS,
    )
  } else {
    toastShow(
      getIntl().formatMessage({ id: 'Load.RecipientsList.UnsubscribeSuccess' }),
      TOAST_VARIANT_SUCCESS,
    )
  }
}

export const deleteRecipient = async (idRecipient: number): Promise<void> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  await beRecipientDeleteRecipient(idList, idRecipient)
}

export const notifyDeletion = (): void => {
  toastShow(
    getIntl().formatMessage({ id: 'Load.RecipientsList.DeleteSuccess' }),
    TOAST_VARIANT_SUCCESS,
  )
}

export const editFilter = ({
  id,
}: Pick<ModalFilterDetailsState, 'id'>): void => {
  if (id !== undefined) {
    const route = `/filters/form/${id}`

    notifyParent('redirectMainFrame', [
      `filters_app_redirector.aspx?r=${encodeURIComponent(route)}`,
    ])
  }
}

export const addGroup = async (name: string): Promise<any> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const existing = await beRecipientGetListOfGroupDetails(idList, {
    searchBy: name,
    searchType: 'EQUALTO',
    pageSize: 1,
    pageNumber: 1,
    orderBy: '',
    orderDescending: false,
  })

  if (existing.TotalItems > 0) {
    return existing.Items[0]
  }

  const newItem = await beRecipientAddGroup({
    IdList: idList,
    Name: name,
    Notes: '',
    Type: 'Normal',
    Deleteable: true,
  })

  return newItem
}

export const fetchModalDeletePreferences = (): Partial<ModalDeleteState> => {
  const prefs = sessionStoreGet(STORE_KEY__RECIPIENTS_LIST_MODAL_DELETE_PREFS)

  if (prefs !== null) {
    return prefs
  }

  return {}
}

export const saveModalDeletePreferences = (
  prefs: Partial<ModalDeleteState>,
): void => {
  sessionStoreSet(STORE_KEY__RECIPIENTS_LIST_MODAL_DELETE_PREFS, prefs)
}

export const unsubscribeRecipientMultilist = async (
  idRecipient: number,
  channelEmail: boolean,
  channelSMS: boolean,
  wipeData: boolean,
  lists: number[],
  allLists: boolean,
  availLists: LabelValuePair[],
): Promise<void> => {
  const qsParams: OptoutRecipientFromMultiListsQsParams = {
    changeFromOptin: true,
    changeFromPending: true,
    changeFromOptout: true,
    webhookSource: 'CONSOLE',
  }

  const dataIn: OptOutMultiListDataIn = {
    IdLists: allLists ? availLists.map((l) => l.value) : lists,
    OptOutType: wipeData ? OPTOUT_TYPE_OBLIO : OPTOUT_TYPE_PLATFORM,
  }

  const apiCalls = [
    channelEmail
      ? beRecipientApiOptoutEmailRecipientFromMultiLists(
          idRecipient,
          qsParams,
          dataIn,
        )
      : undefined,
    channelSMS
      ? beRecipientApiOptoutSmsRecipientFromMultiLists(
          idRecipient,
          qsParams,
          dataIn,
        )
      : undefined,
  ].filter((p) => p !== undefined)

  await Promise.all(apiCalls)
}

export const getRecipientSubscriptionLists = async (
  idRecipient: number,
): Promise<number[]> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  const [emailChannel, smsChannel] = await Promise.all([
    beRecipientApiGetAllSubscriptionsEmailFromList(idRecipient),
    beRecipientApiGetAllSubscriptionsSmsFromList(idRecipient),
  ])

  const emailSubscription: number[] = emailChannel
    .filter((c) => c.IdList === idList || c.Status !== 2) //  keep current list or not unsubscribed
    .map((c) => c.IdList)

  const smsSubscription: number[] = smsChannel
    .filter((c) => c.IdList === idList || c.Status !== 2) //  keep current list or not unsubscribed
    .map((c) => c.IdList)

  return Array.from(new Set([...emailSubscription, ...smsSubscription]))
}
